import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  FormControlLabel,
  Alert
} from '@mui/material';
import { Search as SearchIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { parseISO, startOfDay, endOfDay } from 'date-fns';
import { format } from 'date-fns';

// Componentes extraídos
import HistoricoPagamento from './components/HistoricoPagamento';
import TabelaEmprestimos from './components/TabelaEmprestimos';

// Funções e utilidades
import { formatCurrency, formatDate } from './utils';
import { calcularDiasAtraso } from './utils/calculosUtils';
import { useParcelas } from './hooks/useParcelas';

// Função utilitária para calcular o saldo devedor (parcelas pendentes e atrasadas)
const calcularSaldoDevedor = (emprestimo) => {
  if (!emprestimo || !emprestimo.parcelas) return 0;
  return emprestimo.parcelas
    .filter(p => p.status === 'pendente' || p.status === 'atrasado')
    .reduce((acc, p) => acc + parseFloat(p.valor), 0);
};




export default function Pagamentos() {
  console.log('Pagamentos: Componente renderizado.');

  const location = useLocation();

  // Estados do componente - todos declarados no início
  const [emprestimos, setEmprestimos] = useState([]);
  const [emprestimosFiltrados, setEmprestimosFiltrados] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openHistoricoDialog, setOpenHistoricoDialog] = useState(false);
  const [selectedEmprestimoId, setSelectedEmprestimoId] = useState(null);
  const [historicoPagamento, setHistoricoPagamento] = useState(null);
  const [error, setError] = useState(null);
  const [dataInicial, setDataInicial] = useState('');
  const [dataFinal, setDataFinal] = useState('');

  const [filtrosAplicados, setFiltrosAplicados] = useState({
    dataInicial: '',
    dataFinal: '',
    filtroCliente: '',
    id_emprestimo: null
  });
  const [novasDatasVencimento, setNovasDatasVencimento] = useState({});
  const [jurosPercentual, setJurosPercentual] = useState(0);
  const [descontoPercentual, setDescontoPercentual] = useState(0);
  const [tipoJuros, setTipoJuros] = useState('percentual');
  const [tipoDesconto, setTipoDesconto] = useState('percentual');
  const [valorJurosFixo, setValorJurosFixo] = useState(0);
  const [valorDescontoFixo, setValorDescontoFixo] = useState(0);
  const [aplicarJurosAutomaticosCheckbox, setAplicarJurosAutomaticosCheckbox] = useState(false);
  const [apenasJurosRenovarVencimento, setApenasJurosRenovarVencimento] = useState(false);
  const [jurosFixosPorParcela, setJurosFixosPorParcela] = useState({});
  const [loadingJurosRenovacao, setLoadingJurosRenovacao] = useState(false);
  const [valoresPagos, setValoresPagos] = useState({});
  const [observacoes, setObservacoes] = useState('');


  // Ref para manter a referência mais recente das parcelas memoizadas
  const memoizedParcelasInnerRef = useRef([]);

  // NOVO: Ref para armazenar a última lista de empréstimos que realmente foi definida no estado
  const lastAppliedEmprestimosRef = useRef([]);

  // Derivar selectedEmprestimo do selectedEmprestimoId e da lista de emprestimos
  const selectedEmprestimo = useMemo(() => {
    console.log('Pagamentos useMemo: Recalculando selectedEmprestimo. ID:', selectedEmprestimoId, 'Emprestimos length:', emprestimos.length);
    return emprestimos.find(emp => emp.id === selectedEmprestimoId) || null;
  }, [selectedEmprestimoId, emprestimos]);

  // NOVO: Criar uma versão memoizada das parcelas para o useParcelas, garantindo estabilidade de referência
  // Esta memo garantirá que a referência do array passado para useParcelas só mude se os IDs das parcelas mudarem
  const memoizedParcelas = useMemo(() => {
    const currentParcelas = selectedEmprestimo?.parcelas || [];
    const previousParcelas = memoizedParcelasInnerRef.current; // Ref para o array anterior

    // Se as referências são as mesmas, não há necessidade de comparar o conteúdo
    if (previousParcelas === currentParcelas) {
      console.log('Pagamentos useMemo: Referências das parcelas são as mesmas, retornando a atual.');
      return currentParcelas;
    }

    // Comparação profunda do conteúdo das parcelas (IDs, valor, status)
    const areContentsSame = previousParcelas.length === currentParcelas.length &&
      previousParcelas.every((prevP, index) => {
        const currentP = currentParcelas[index];
        // Comparar propriedades essenciais para identificar uma mudança no estado da parcela
        return prevP.id === currentP.id &&
          parseFloat(prevP.valor) === parseFloat(currentP.valor) &&
          prevP.status === currentP.status; // Adicionado status para detectar pagamento
      });

    if (areContentsSame) {
      console.log('Pagamentos useMemo: Conteúdo das parcelas é o mesmo, retornando referência anterior.');
      return previousParcelas; // Retorna a referência estável anterior
    } else {
      console.log('Pagamentos useMemo: Conteúdo das parcelas mudou, atualizando referência.');
      memoizedParcelasInnerRef.current = currentParcelas; // Atualiza a ref com o novo array
      return currentParcelas; // Retorna a nova referência do array
    }
  }, [selectedEmprestimo]); // Depende do objeto selectedEmprestimo (que já é memoizado)

  // Hook personalizado para gerenciar o estado das parcelas
  const {
    selectedParcelas,
    valoresCalculados,
    valoresPagamento,
    setValoresPagamento,
    setValoresCalculados,
    selectAllParcelas,
    deselectAllParcelas,
    recalcularTodosValores,
    toggleParcelaSelection
  } = useParcelas(memoizedParcelas);

  console.log('Pagamentos: selectedEmprestimo mudou para:', selectedEmprestimo);
  console.log('Pagamentos: selectedParcelas (do hook) é:', selectedParcelas);

  // Função para carregar os dados de empréstimos (para atualizações forçadas e carga inicial)
  const carregarDadosEmprestimos = useCallback(async (forcarAtualizacao = false, currentFiltros = null) => {
    // Se currentFiltros for null, use filtrosAplicados do escopo
    const filtrosParaUsar = currentFiltros || filtrosAplicados;
    console.log('Pagamentos carregarDadosEmprestimos: Iniciando carregamento de dados. Forçar atualização:', forcarAtualizacao, 'Filtros:', filtrosParaUsar);
    try {
      let dataToProcess = [];
      const cachedData = localStorage.getItem('emprestimos');
      if (cachedData && !forcarAtualizacao) {
        const parsedData = JSON.parse(cachedData);
        dataToProcess = parsedData;
        console.log('Pagamentos carregarDadosEmprestimos: Dados carregados do cache');
      } else {
        console.log('Pagamentos carregarDadosEmprestimos: Buscando dados atualizados do servidor...');

        const params = new URLSearchParams({
          incluir_parcelas: true,
          _: Date.now(), // Para evitar cache do navegador
        });

        if (filtrosParaUsar.dataInicial) {
          params.append('data_inicial', filtrosParaUsar.dataInicial);
        }
        if (filtrosParaUsar.dataFinal) {
          params.append('data_final', filtrosParaUsar.dataFinal);
        }
        if (filtrosParaUsar.filtroCliente) {
          params.append('nome_cliente', filtrosParaUsar.filtroCliente);
        }

        const response = await api.get(`/emprestimos?${params.toString()}`);
        console.log('Pagamentos carregarDadosEmprestimos: Resposta da API de empréstimos:', response.data);
        dataToProcess = response.data;
      }

      // Filtrar apenas empréstimos ativos
      dataToProcess = dataToProcess.filter(emp => emp.status === 'ativo');
      console.log('Pagamentos carregarDadosEmprestimos: Após filtro de status ativo:', dataToProcess.map(e => ({ id: e.id, cliente: e.cliente?.nome, status: e.status })));

      console.log('Pagamentos carregarDadosEmprestimos: Dados brutos da API (antes do filtro de pendentes):', dataToProcess.map(e => ({ id: e.id, cliente: e.cliente?.nome, parcelas_count: e.parcelas?.length, parcelas_status: e.parcelas?.map(p => p.status) })));

      const emprestimosComParcelasPendentes = dataToProcess
        .map(emp => {
          return {
            ...emp,
            parcelas: emp.parcelas
              ? emp.parcelas
                .filter(p => p.status === 'pendente' || p.status === 'atrasado' || p.status === 'pago_parcial')
                .map(p => ({
                  ...p,
                  valor_original: p.valor_original ?? p.valor
                }))
              : []
          };
        })
        .filter(emp => emp.parcelas && emp.parcelas.length > 0);

      console.log('Pagamentos carregarDadosEmprestimos: Após filtro de status (pendente/atrasado):', emprestimosComParcelasPendentes.map(e => ({ id: e.id, cliente: e.cliente?.nome, parcelas_count: e.parcelas?.length, parcelas_status: e.parcelas?.map(p => p.status) })));

      // Aplicar filtros de cliente e data
      let filteredData = emprestimosComParcelasPendentes;

      if (filtrosParaUsar.filtroCliente) {
        const lowerCaseFiltro = filtrosParaUsar.filtroCliente.toLowerCase();
        filteredData = filteredData.filter(emp =>
          emp.cliente?.nome?.toLowerCase().includes(lowerCaseFiltro)
        );
        console.log('Pagamentos carregarDadosEmprestimos: Após filtro de cliente:', filteredData.map(e => ({ id: e.id, cliente: e.cliente?.nome })));
      }

      if (filtrosParaUsar.dataInicial) {
        console.log('Pagamentos carregarDadosEmprestimos: Filtro Data Inicial String:', filtrosParaUsar.dataInicial);
        // Converte a string YYYY-MM-DD para um objeto Date e obtém o início do dia local
        const startDate = startOfDay(parseISO(filtrosParaUsar.dataInicial));
        console.log('Pagamentos carregarDadosEmprestimos: Filtro Data Inicial Objeto (Local Start Day):', startDate.toISOString());
        filteredData = filteredData.filter(emp =>
          emp.parcelas.some(parcela => {
            const vencimentoDateFromAPI = parseISO(parcela.data_vencimento); // UTC date object from API
            // Create a local date object from the UTC date's year, month, and day components
            const localVencimentoDay = new Date(
              vencimentoDateFromAPI.getFullYear(),
              vencimentoDateFromAPI.getMonth(),
              vencimentoDateFromAPI.getDate()
            );
            const vencimento = startOfDay(localVencimentoDay);
            console.log(`Pagamentos carregarDadosEmprestimos: Parcela ${parcela.id} - Vencimento API: ${parcela.data_vencimento}, Vencimento Objeto (Local Start Day from UTC): ${vencimento.toISOString()}, Comparando com StartDate: ${startDate.toISOString()}`);
            return vencimento.getTime() >= startDate.getTime();
          })
        );
        console.log('Pagamentos carregarDadosEmprestimos: Após filtro Data Inicial:', filteredData.map(e => ({ id: e.id, cliente: e.cliente?.nome, parcelas_count: e.parcelas?.length })));
      }

      if (filtrosParaUsar.dataFinal) {
        console.log('Pagamentos carregarDadosEmprestimos: Filtro Data Final String:', filtrosParaUsar.dataFinal);
        // Converte a string YYYY-MM-DD para um objeto Date e obtém o fim do dia local
        const endDate = endOfDay(parseISO(filtrosParaUsar.dataFinal));
        console.log('Pagamentos carregarDadosEmprestimos: Filtro Data Final Objeto (Local End Day):', endDate.toISOString());
        filteredData = filteredData.filter(emp =>
          emp.parcelas.some(parcela => {
            const vencimentoDateFromAPI = parseISO(parcela.data_vencimento); // UTC date object from API
            // Create a local date object from the UTC date's year, month, and day components
            const localVencimentoDay = new Date(
              vencimentoDateFromAPI.getFullYear(),
              vencimentoDateFromAPI.getMonth(),
              vencimentoDateFromAPI.getDate()
            );
            const vencimento = endOfDay(localVencimentoDay);
            console.log(`Pagamentos carregarDadosEmprestimos: Parcela ${parcela.id} - Vencimento API: ${parcela.data_vencimento}, Vencimento Objeto (Local End Day from UTC): ${vencimento.toISOString()}, Comparando com EndDate: ${endDate.toISOString()}`);
            return vencimento.getTime() <= endDate.getTime();
          })
        );
        console.log('Pagamentos carregarDadosEmprestimos: Após filtro Data Final:', filteredData.map(e => ({ id: e.id, cliente: e.cliente?.nome, parcelas_count: e.parcelas?.length })));
      }

      console.log('Pagamentos carregarDadosEmprestimos: Dados filtrados FINAIS antes de setar estado:', filteredData.map(e => ({ id: e.id, cliente: e.cliente?.nome, parcelas_count: e.parcelas?.length, parcelas_status: e.parcelas?.map(p => p.status) })));

      // Comparação profunda para evitar re-renderizações desnecessárias
      const areEmprestimosFunctionallySame = (oldEmprestimos, newEmprestimos) => {
        if (oldEmprestimos.length !== newEmprestimos.length) return false;

        // Sort arrays by ID to ensure consistent order for comparison
        const sortedOldEmprestimos = [...oldEmprestimos].sort((a, b) => a.id - b.id);
        const sortedNewEmprestimos = [...newEmprestimos].sort((a, b) => a.id - b.id);

        return sortedOldEmprestimos.every((oldEmp, index) => {
          const newEmp = sortedNewEmprestimos[index]; // Get by index after sorting
          if (!newEmp || oldEmp.id !== newEmp.id) return false; // Ensure IDs match after sort

          // Comparar propriedades essenciais do empréstimo
          if (oldEmp.cliente?.nome !== newEmp.cliente?.nome ||
            parseFloat(oldEmp.valor) !== parseFloat(newEmp.valor) || // Use parseFloat for consistency
            (oldEmp.data ? parseISO(oldEmp.data).getTime() : null) !== (newEmp.data ? parseISO(newEmp.data).getTime() : null)) { // Compare date time values
            return false;
          }

          // Comparar parcelas profundamente
          if (oldEmp.parcelas.length !== newEmp.parcelas.length) return false;

          const sortedOldParcelas = [...oldEmp.parcelas].sort((a, b) => a.id - b.id);
          const sortedNewParcelas = [...newEmp.parcelas].sort((a, b) => a.id - b.id);

          return sortedOldParcelas.every((oldParcela, pIndex) => {
            const newParcela = sortedNewParcelas[pIndex];
            if (!newParcela || oldParcela.id !== newParcela.id) return false;

            return parseFloat(oldParcela.valor) === parseFloat(newParcela.valor) &&
              oldParcela.status === newParcela.status &&
              (oldParcela.data_vencimento ? parseISO(oldParcela.data_vencimento).getTime() : null) === (newParcela.data_vencimento ? parseISO(newParcela.data_vencimento).getTime() : null); // Compare date time values
          });
        });
      };

      if (!areEmprestimosFunctionallySame(lastAppliedEmprestimosRef.current, filteredData)) {
        console.log('Pagamentos carregarDadosEmprestimos: Empréstimos mudaram funcionalmente, atualizando estados.');
        setEmprestimos(filteredData);
        lastAppliedEmprestimosRef.current = filteredData; // Atualiza a ref
      } else {
        console.log('Pagamentos carregarDadosEmprestimos: Empréstimos não mudaram funcionalmente, evitando atualização de estado.');
      }

      console.log('Pagamentos carregarDadosEmprestimos: Dados de pagamentos atualizados.');
      return filteredData; // Retorna os dados atualizados da API
    } catch (err) {
      console.error('Pagamentos carregarDadosEmprestimos: Erro ao carregar empréstimos:', err);
      setError('Erro ao carregar empréstimos.');
    } finally {
      console.log('Pagamentos carregarDadosEmprestimos: Carregamento de dados finalizado.');
    }
  }, [setEmprestimos, filtrosAplicados]);

  // Funções auxiliares para formatação de data para o backend


  // Função para processar pagamento
  const handlePagamento = useCallback(async (pagamentoData) => {
    console.log('Pagamentos handlePagamento: Iniciando pagamento com dados:', pagamentoData);
    console.log('Pagamentos handlePagamento: Parcelas selecionadas ANTES do envio:', selectedParcelas);
    if (selectedParcelas.length === 0) {
      toast.warning('Selecione pelo menos uma parcela para realizar o pagamento');
      console.warn('Pagamentos handlePagamento: Nenhuma parcela selecionada. Abortando.');
      return;
    }
    try {
      const payload = {
        emprestimo_id: pagamentoData?.emprestimo_id || (selectedEmprestimo && selectedEmprestimo.id),
        parcelas: pagamentoData?.parcelas || [],
        observacoes: pagamentoData?.observacoes || '',
        total_pago: pagamentoData?.total_pago,
        saldo_restante: pagamentoData?.saldo_restante
      };

      // Se pagamentoData não tiver parcelas, construa-las a partir de selectedParcelas
      if (!payload.parcelas || payload.parcelas.length === 0) {
        payload.parcelas = selectedParcelas.map(p => {
          // Determinar o valor correto de juros manuais a enviar
          let jurosManuaisValor = 0;
          if (apenasJurosRenovarVencimento) {
            // Prioridade para o juro calculado pelo histórico/backend para renovação
            jurosManuaisValor = jurosFixosPorParcela[p.id] || 0;
          } else {
            // Caso contrário, usa o valor fixo global ou calculado
            jurosManuaisValor = Number(valorJurosFixo) || valoresCalculados[p.id]?.jurosManuais || 0;
          }

          return {
            id: p.id,
            valor_pago: valoresPagos[p.id] || 0,
            valor_devido_frontend: valoresCalculados[p.id],
            nova_data_vencimento: novasDatasVencimento[p.id],
            valor_devido_calculado_frontend: valoresCalculados[p.id] || p.valor,
            juros_automaticos_valor: valoresCalculados[p.id]?.jurosAutomaticos || 0,
            juros_manuais_valor: jurosManuaisValor,
            desconto_valor: valoresCalculados[p.id]?.desconto || 0,
            juros_automaticos_percentual: valoresCalculados[p.id]?.jurosAutomaticosPercentual || 0,
            juros_manuais_percentual: valoresCalculados[p.id]?.jurosManuaisPercentual || 0,
            desconto_percentual: valoresCalculados[p.id]?.descontoPercentual || 0,
          };
        });
      }

      console.log('DEBUG: Payload final enviado para API:', payload);
      if (!payload.emprestimo_id) {
        toast.error('ID do empréstimo não encontrado!');
        return;
      }
      if (!payload.parcelas || payload.parcelas.length === 0) {
        toast.error('Selecione pelo menos uma parcela para pagar.');
        return;
      }

      // Log detalhado do payload de cada parcela
      console.log('DEBUG: Parcelas enviadas para API:', payload.parcelas.map(p => ({ id: p.id, valor_pago: p.valor_pago, valor_devido_frontend: p.valor_devido_frontend, nova_data_vencimento: p.nova_data_vencimento })));

      const { emprestimo_id, parcelas, observacoes, total_pago, saldo_restante } = payload;
      await api.post(`/emprestimos/${emprestimo_id}/pagamentos`, {
        parcelas,
        observacoes,
        total_pago,
        saldo_restante,
        saldo_total_calculado: typeof saldo_restante !== 'undefined' ? saldo_restante : total_pago
      });

      // Limpar estados relacionados ao pagamento
      setValoresCalculados({});
      setObservacoes('');
      setNovasDatasVencimento({});
      setJurosPercentual(0);
      setDescontoPercentual(0);

      // Chamar a função unificada de carregamento e obter a nova lista de empréstimos
      await carregarDadosEmprestimos(true);
      console.log('Pagamentos handlePagamento: Dados iniciais recarregados.');

      // Feedback de sucesso e fechar modal
      toast.success('Pagamento e Renovação realizados com sucesso!');
      handleCloseDialog();

    } catch (error) {
      console.error('Pagamentos handlePagamento: Erro ao registrar pagamento:', error);
      toast.error('Erro ao registrar pagamento. Verifique os dados e tente novamente.');
    }
  }, [selectedParcelas, valoresPagamento, valoresCalculados, novasDatasVencimento, carregarDadosEmprestimos, apenasJurosRenovarVencimento, valoresPagos, valorJurosFixo, jurosFixosPorParcela, selectedEmprestimo, setValoresCalculados]);

  // UseEffect para limpar selectedEmprestimoId se o empréstimo selecionado não existir mais na lista
  useEffect(() => {
    // Só limpa se um ID estiver selecionado mas o objeto do empréstimo correspondente for null
    if (selectedEmprestimoId !== null && selectedEmprestimo === null) {
      console.log('Pagamentos useEffect (clean-up): selectedEmprestimo é null mas selectedEmprestimoId está definido. Limpando selectedEmprestimoId.');
      setSelectedEmprestimoId(null);
    }
  }, [selectedEmprestimoId, selectedEmprestimo, setSelectedEmprestimoId]); // Depende dos estados e setter

  // Modificar handleAplicarTaxas para buscar juros do backend
  const handleAplicarTaxas = useCallback(async () => {
    console.log('Pagamentos handleAplicarTaxas: Aplicando taxas...');

    if (selectedParcelas.length === 0) {
      toast.warning('Selecione pelo menos uma parcela antes de aplicar taxas.');
      console.warn('Pagamentos handleAplicarTaxas: Nenhuma parcela selecionada. Abortando aplicação de taxas.');
      return;
    }

    try {
      // Se juros automáticos estiverem ativos, buscar do backend
      if (aplicarJurosAutomaticosCheckbox) {
        console.log('Pagamentos handleAplicarTaxas: Buscando juros automáticos do backend...');

        // Buscar juros automáticos para cada parcela selecionada
        const jurosAutomaticos = {};
        for (const parcela of selectedParcelas) {
          try {
            const response = await api.get(`/parcelas/${parcela.id}/juros-automaticos`);
            if (response.status !== 200) {
              throw new Error(`Erro ao buscar juros automáticos: ${response.statusText}`);
            }
            const data = response.data;
            jurosAutomaticos[parcela.id] = data;
            console.log(`Juros automáticos para parcela ${parcela.id}:`, data);
          } catch (error) {
            console.error(`Erro ao buscar juros automáticos para parcela ${parcela.numero_parcela}:`, error);
            toast.error(`Erro ao buscar juros automáticos para parcela ${parcela.numero_parcela}`);
          }
        }

        // Atualizar os valores calculados com os juros do backend
        setValoresCalculados(prev => {
          const novosValores = { ...prev };
          Object.entries(jurosAutomaticos).forEach(([parcelaId, juros]) => {
            const parcelaOriginal = selectedParcelas.find(p => p.id === Number(parcelaId));
            novosValores[parcelaId] = {
              ...(novosValores[parcelaId] || {}),
              jurosAutomaticos: juros.valor,
              jurosAutomaticosPercentual: juros.percentual,
              valorFinal: ((novosValores[parcelaId]?.valorOriginal ?? parcelaOriginal?.valor) * 1) + juros.valor,
              detalhes: {
                ...(novosValores[parcelaId]?.detalhes || {}),
                taxa: juros.taxa,
                periodicidade: juros.periodicidade
              }
            };
          });
          return novosValores;
        });
        // Propagar taxa e periodicidade para selectedParcelas
        // Nota: selectedParcelas é gerenciado pelo hook useParcelas
        // As taxas e periodicidades serão aplicadas durante o processamento
      }

      // Chamar recalcularTodosValores do useParcelas para que ele faça o trabalho pesado
      const totaisCalculados = await recalcularTodosValores({
        parcelasParaProcessar: selectedParcelas,
        tipoJuros,
        jurosPercentual,
        valorJurosFixo,
        tipoDesconto,
        descontoPercentual,
        valorDescontoFixo,
        aplicarJurosAutomaticos: aplicarJurosAutomaticosCheckbox
      });

      if (totaisCalculados) {
        console.log('Pagamentos handleAplicarTaxas: Totais calculados após taxas:', totaisCalculados);

        // ATUALIZAR valoresCalculados com os novos valores calculados
        setValoresCalculados(prev => {
          const novosValores = { ...prev };
          selectedParcelas.forEach(parcela => {
            // USAR valor atual da parcela (1100) que já inclui juros originais
            const valorAtual = parseFloat(parcela.valor || 0);
            const jurosManuais = tipoJuros === 'fixo' ? parseFloat(valorJurosFixo || 0) : (valorAtual * parseFloat(jurosPercentual || 0) / 100);
            const desconto = tipoDesconto === 'fixo' ? parseFloat(valorDescontoFixo || 0) : (valorAtual * parseFloat(jurosPercentual || 0) / 100);

            // USAR juros automáticos do estado anterior (que foram buscados do backend)
            const jurosAutomaticos = prev[parcela.id]?.jurosAutomaticos || 0;

            // SEMPRE ADICIONAR juros ao valor atual, NUNCA subtrair
            const valorFinal = valorAtual + jurosManuais + jurosAutomaticos - desconto;

            console.log(`🔄 Parcela ${parcela.id}: valorAtual=${valorAtual}, jurosManuais=${jurosManuais}, jurosAutomaticos=${jurosAutomaticos}, desconto=${desconto}, valorFinal=${valorFinal}`);

            novosValores[parcela.id] = {
              ...(novosValores[parcela.id] || {}),
              valorOriginal: valorAtual,
              jurosManuais,
              jurosAutomaticos,
              desconto,
              valorFinal,
              detalhes: {
                ...(novosValores[parcela.id]?.detalhes || {}),
                valorOriginal: valorAtual,
                jurosManuais,
                jurosAutomaticos,
                desconto,
                valorFinal
              }
            };
          });
          return novosValores;
        });

        // ATUALIZAR valoresPagamento para que o campo "Valor a Pagar" seja atualizado
        setValoresPagamento(prev => {
          console.log('DEBUG: Atualizando valoresPagamento. Estado anterior:', prev);
          const novosValores = { ...prev };

          // ATUALIZAR TODAS AS PARCELAS DO EMPRÉSTIMO, não apenas as selecionadas
          const todasParcelas = selectedEmprestimo?.parcelas || [];
          console.log('DEBUG: Todas as parcelas do empréstimo:', todasParcelas.map(p => ({
            id: p.id,
            numero: p.numero_parcela,
            valor_original: p.valor_original,
            valor: p.valor
          })));

          todasParcelas.forEach(parcela => {
            // USAR valor atual da parcela (1100) que já inclui juros originais
            const valorAtual = parseFloat(parcela.valor || 0);
            const jurosManuais = tipoJuros === 'fixo' ? parseFloat(valorJurosFixo || 0) : (valorAtual * parseFloat(jurosPercentual || 0) / 100);
            const desconto = tipoDesconto === 'fixo' ? parseFloat(valorDescontoFixo || 0) : (valorAtual * parseFloat(jurosPercentual || 0) / 100);

            // USAR juros automáticos do estado valoresCalculados
            const jurosAutomaticos = valoresCalculados[parcela.id]?.jurosAutomaticos || 0;

            // SEMPRE ADICIONAR juros ao valor atual, NUNCA subtrair
            const valorFinal = valorAtual + jurosManuais + jurosAutomaticos - desconto;
            novosValores[parcela.id] = valorFinal;

            console.log(`🔄 valoresPagamento - Parcela ${parcela.id}: valorAtual=${valorAtual}, jurosManuais=${jurosManuais}, jurosAutomaticos=${jurosAutomaticos}, desconto=${desconto}, valorFinal=${valorFinal}`);
          });
          console.log('DEBUG: Novos valoresPagamento:', novosValores);
          return novosValores;
        });

        // ATUALIZAR valoresPagos para que o campo "Valor Pago" receba o mesmo valor
        setValoresPagos(prev => {
          console.log('DEBUG: Atualizando valoresPagos. Estado anterior:', prev);
          const novosValores = { ...prev };

          // ATUALIZAR TODAS AS PARCELAS DO EMPRÉSTIMO
          const todasParcelas = selectedEmprestimo?.parcelas || [];

          todasParcelas.forEach(parcela => {
            // CALCULAR o valor final diretamente (não depender de valoresCalculados)
            const valorAtual = parseFloat(parcela.valor || 0);
            const jurosManuais = tipoJuros === 'fixo' ? parseFloat(valorJurosFixo || 0) : (valorAtual * parseFloat(jurosPercentual || 0) / 100);
            const desconto = tipoDesconto === 'fixo' ? parseFloat(valorDescontoFixo || 0) : (valorAtual * parseFloat(descontoPercentual || 0) / 100);

            // SEMPRE ADICIONAR juros ao valor atual, NUNCA subtrair
            const valorFinal = valorAtual + jurosManuais - desconto;
            novosValores[parcela.id] = valorFinal;

            console.log(`🔄 valoresPagos - Parcela ${parcela.id}: valorAtual=${valorAtual}, jurosManuais=${jurosManuais}, desconto=${desconto}, valorFinal=${valorFinal}`);
          });
          console.log('DEBUG: Novos valoresPagos:', novosValores);
          return novosValores;
        });

        toast.success('Taxas aplicadas com sucesso!');
      } else {
        toast.error('Erro ao aplicar taxas. Verifique o console para detalhes.');
      }

    } catch (error) {
      console.error('Erro ao aplicar taxas:', error);
      toast.error('Erro ao aplicar taxas. Por favor, tente novamente.');
    }
  }, [selectedParcelas, tipoJuros, jurosPercentual, valorJurosFixo, tipoDesconto, descontoPercentual, valorDescontoFixo, aplicarJurosAutomaticosCheckbox, recalcularTodosValores, setValoresPagamento, selectedEmprestimo?.parcelas, setValoresCalculados, valoresCalculados]);

  // Memoized callbacks para ModalPagamento
  const selecionarTodasParcelas = useCallback(() => {
    console.log('Pagamentos selecionarTodasParcelas: Selecionando todas as parcelas.');
    selectAllParcelas();
  }, [selectAllParcelas]);

  const removerTodasSelecoes = useCallback(() => {
    console.log('Pagamentos removerTodasSelecoes: Removendo todas as seleções.');
    deselectAllParcelas();
  }, [deselectAllParcelas]);

  // Função para abrir o modal de pagamento
  const handleOpenDialog = useCallback((emprestimo) => {
    console.log('Pagamentos handleOpenDialog: Abrindo diálogo para empréstimo:', emprestimo);
    setSelectedEmprestimoId(emprestimo.id); // Definir apenas o ID
    setOpenDialog(true);
    // Limpar estados ao abrir o modal, se necessário
    setValoresCalculados({});
    setObservacoes('');
    setNovasDatasVencimento({});
    setJurosPercentual(0);
    setDescontoPercentual(0);
    deselectAllParcelas(); // Garantir que nenhuma parcela esteja selecionada ao abrir
  }, [deselectAllParcelas, setValoresCalculados]);

  // Função para fechar o modal de pagamento
  const handleCloseDialog = useCallback(() => {
    console.log('Pagamentos handleCloseDialog: Fechando dialog.');
    setOpenDialog(false);
    setSelectedEmprestimoId(null); // Limpa o empréstimo selecionado ao fechar o modal
    deselectAllParcelas(); // Garante que as parcelas selecionadas sejam limpas
    setValoresPagamento({}); // Limpa os valores de pagamento
    setValoresCalculados({}); // Limpa os valores calculados
    setObservacoes(''); // Limpa observações
    setNovasDatasVencimento({}); // Limpa novas datas de vencimento
    setJurosPercentual(0); // Limpa juros
    setDescontoPercentual(0); // Limpa desconto
  }, [deselectAllParcelas, setValoresPagamento, setValoresCalculados, setNovasDatasVencimento, setJurosPercentual, setDescontoPercentual]);

  // Função para abrir o modal de histórico de pagamentos
  const handleOpenHistorico = useCallback(async (parcelaId) => {
    setOpenHistoricoDialog(true);
    setHistoricoPagamento(null); // Limpa o histórico anterior
    try {
      let response = await api.get(`/pagamentos/${parcelaId}/historico`);
      let historico = response.data;
      let avisoFilha = null;
      // Detecta se é filha de pagamento parcial
      if (
        historico && historico.parcela && historico.parcela.observacoes &&
        /criada a partir do pagamento parcial da parcela #(\d+)/i.test(historico.parcela.observacoes)
      ) {
        const match = historico.parcela.observacoes.match(/criada a partir do pagamento parcial da parcela #(\d+)/i);
        if (match && match[1]) {
          avisoFilha = `Esta parcela foi criada a partir de um pagamento parcial. Exibindo histórico da parcela original #${match[1]}.`;
          // Buscar histórico da parcela original
          const emprestimoId = historico.parcela.id_emprestimo;
          // Buscar todas as parcelas do empréstimo para encontrar o id da original
          const emprestimoResp = await api.get(`/emprestimos?incluir_parcelas=true`);
          const emprestimo = Array.isArray(emprestimoResp.data)
            ? emprestimoResp.data.find(e => e.id === emprestimoId)
            : null;
          let parcelaOriginal = null;
          if (emprestimo && Array.isArray(emprestimo.parcelas)) {
            parcelaOriginal = emprestimo.parcelas.find(p => String(p.numero_parcela) === match[1]);
          }
          if (parcelaOriginal) {
            response = await api.get(`/pagamentos/${parcelaOriginal.id}/historico`);
            historico = response.data;
            historico._avisoFilha = avisoFilha;
          } else {
            historico._avisoFilha = avisoFilha + ' (Não foi possível localizar a parcela original)';
          }
        }
      }
      if (historico && historico.parcela) {
        historico.valor_original = historico.parcela.valor;
        historico.valor_juros_automaticos = historico.parcela.valor_juros;
        if (historico.valor_original) {
          if (!historico.juros_automaticos_percentual) {
            historico.juros_automaticos_percentual = ((historico.valor_juros_automaticos || 0) / historico.valor_original * 100).toFixed(2);
          }
          if (!historico.juros_manuais_percentual) {
            historico.juros_manuais_percentual = ((historico.total_juros || 0) / historico.valor_original * 100).toFixed(2);
          }
          if (!historico.desconto_percentual) {
            historico.desconto_percentual = ((historico.total_desconto || 0) / historico.valor_original * 100).toFixed(2);
          }
        }
      }
      setHistoricoPagamento(historico);
    } catch (error) {
      console.error('Erro ao buscar histórico:', error);
      toast.error('Erro ao buscar histórico de pagamento');
    }
  }, []);

  // UseEffect para filtrar empréstimos com base no nome do cliente
  useEffect(() => {
    console.log('Pagamentos useEffect: Filtrando empréstimos. Filtro:', filtro, 'Empréstimos:', emprestimos.length);
    // O filtro é aplicado diretamente na renderização
  }, [filtro, emprestimos]);

  // UseEffect para carregar os dados iniciais na montagem do componente
  useEffect(() => {
    console.log('Pagamentos useEffect: Chamando carregarDadosEmprestimos na montagem (uma vez).');
    carregarDadosEmprestimos(); // Chama sem filtros aqui, pois eles serão aplicados via 'aplicarFiltros'
  }, [carregarDadosEmprestimos]);

  // UseEffect para verificar se deve abrir o modal automaticamente
  useEffect(() => {
    if (location.state?.abrirModal && location.state?.clienteSelecionado) {
      const cliente = location.state.clienteSelecionado;
      console.log('Pagamentos useEffect: Abrindo modal automaticamente para cliente:', cliente);

      // Buscar o empréstimo do cliente e abrir o modal
      const buscarEmprestimoECarregar = async () => {
        try {
          const response = await api.get(`/emprestimos?cliente_id=${cliente.id}&status=ativo&incluir_parcelas=true`);
          const emprestimos = response.data;

          if (emprestimos.length > 0) {
            const emprestimo = emprestimos[0];
            setSelectedEmprestimoId(emprestimo.id);
            setOpenDialog(true);

            // Limpar o estado da navegação
            window.history.replaceState({}, document.title);
          } else {
            toast.error('Nenhum empréstimo ativo encontrado para este cliente');
          }
        } catch (error) {
          console.error('Erro ao buscar empréstimo:', error);
          toast.error('Erro ao abrir modal de pagamentos');
        }
      };

      buscarEmprestimoECarregar();
    }
  }, [location.state]);

  // Exibe o último log de pagamento salvo ao carregar a página - MOVIDO PARA O FINAL
  useEffect(() => {
    console.log('Pagamentos useEffect: Executado para log de pagamento (no final).');
    const ultimoLog = localStorage.getItem('ultimoLogPagamento');
    if (ultimoLog) {
      alert('Último log de pagamento salvo (copie antes de fechar):\n' + ultimoLog);
      localStorage.removeItem('ultimoLogPagamento');
    }
  }, []);

  // Função para fechar o modal de histórico de pagamentos
  const handleCloseHistoricoDialog = useCallback(() => {
    console.log('Pagamentos handle handleCloseHistoricoDialog: Fechando histórico de pagamento.');
    setOpenHistoricoDialog(false);
    setHistoricoPagamento(null); // Limpa o histórico ao fechar
  }, []);



  // eslint-disable-next-line react-hooks/exhaustive-deps

  // Atualiza o valor pago de uma parcela
  const handleValorPagoChange = useCallback((parcelaId, valor) => {
    console.log('Pagamentos handleValorPagoChange: Atualizando valor pago da parcela:', parcelaId, 'para', valor);
    setValoresPagos(prev => ({
      ...prev,
      [parcelaId]: valor
    }));
  }, [setValoresPagos]);

  // Atualiza a nova data de vencimento de uma parcela
  const handleNovaDataVencimentoChange = useCallback((parcelaId, data) => {
    console.log('Pagamentos handleNovaDataVencimentoChange: Atualizando nova data de vencimento da parcela:', parcelaId, 'para', data);
    setNovasDatasVencimento(prev => ({
      ...prev,
      [parcelaId]: data
    }));
  }, [setNovasDatasVencimento]);

  // Calcula o valor total das parcelas selecionadas
  const calcularValorTotal = useCallback(() => {
    let total = 0;
    selectedParcelas.forEach(parcela => {
      if (apenasJurosRenovarVencimento) {
        // CORREÇÃO: No modo "apenas juros", usar valoresPagos (apenas juros) em vez de valoresPagamento
        total += parseFloat(valoresPagos[parcela.id]) || 0;
      } else {
        total += parseFloat(valoresCalculados[parcela.id]?.valorFinal || 0);
      }
    });
    return total;
  }, [selectedParcelas, valoresCalculados, valoresPagos, apenasJurosRenovarVencimento]);



  // Modificar o useEffect que monitora aplicarJurosAutomaticosCheckbox
  useEffect(() => {
    if (selectedParcelas.length > 0 && aplicarJurosAutomaticosCheckbox) {
      console.log('Pagamentos useEffect (aplicarJurosAutomaticosCheckbox): Buscando juros automáticos do backend para parcelas selecionadas:', selectedParcelas);
      // Aqui você deve chamar a API do backend para buscar os juros automáticos
      // Exemplo: const jurosAutomaticos = await buscarJurosAutomaticosDoBackend(selectedParcelas);
      // Depois, atualize os valores calculados com os juros retornados pelo backend
      // Exemplo: setValoresCalculados(prev => ({ ...prev, ...jurosAutomaticos }));
    }
  }, [selectedParcelas, aplicarJurosAutomaticosCheckbox]);

  // 1. useEffect para aplicar lógica ao marcar/desmarcar 'Apenas juros e renovar vencimento'
  useEffect(() => {
    async function buscarJurosRenovacao() {
      if (apenasJurosRenovarVencimento && selectedParcelas.length > 0) {
        setLoadingJurosRenovacao(true);
        const novosJuros = {};
        const novosValoresPagamento = { ...valoresPagamento };
        const novosValoresPagos = { ...valoresPagamento }; // Renomeado para evitar conflito com valoresPagamento
        for (const parcela of selectedParcelas) {
          try {
            const resp = await api.get(`/pagamentos/${parcela.id}/historico`);
            // const historico = resp.data;
            // Some todos os campos de juros relevantes (contrato, aportes, automáticos, manuais)
            // SEMPRE usar o valor_juros já calculado e armazenado no banco
            let totalJuros = parcela.valor_juros !== undefined && parcela.valor_juros !== null
              ? Number(parcela.valor_juros)
              : 0;

            // CORREÇÃO: Se o valor_juros for 0 (legado/erro), tentar calcular baseado na taxa do empréstimo
            if (totalJuros === 0 && selectedEmprestimo && selectedEmprestimo.taxa_juros) {
              const taxa = Number(selectedEmprestimo.taxa_juros);
              const valorTotal = Number(parcela.valor);
              // Se a taxa for > 0 e o valor total > 0
              if (taxa > 0 && valorTotal > 0) {
                // Cálculo reverso: ValorTotal = Principal + (Principal * Taxa/100)
                // ValorTotal = Principal * (1 + Taxa/100)
                // Principal = ValorTotal / (1 + Taxa/100)
                // Juros = ValorTotal - Principal
                const principalCalculado = valorTotal / (1 + (taxa / 100));
                totalJuros = valorTotal - principalCalculado;
                console.log(`[DEBUG RENOVAÇÃO] Juros calculados automaticamente (Taxa ${taxa}%): R$ ${totalJuros.toFixed(2)}`);
              }
            }

            const valorParcela = Number(parcela.valor_original || parcela.valor || 0);
            // DEBUG: Log para verificar os valores da parcela
            console.log(`DEBUG RENOVAÇÃO - Parcela ${parcela.id}:`, {
              parcela_valor_original: parcela.valor_original,
              parcela_valor: parcela.valor,
              valorParcela_calculado: valorParcela,
              totalJuros,
              valorTotalAPagar: valorParcela + Number(totalJuros)
            });

            // CORREÇÃO: Quando apenas juros são pagos, a nova parcela deve manter o valor original
            // Se totalJuros > 0, significa que apenas juros foram pagos, então manter valor original
            const valorParaNovaParcela = totalJuros > 0 ? valorParcela : valorParcela + Number(totalJuros);

            // const valorTotalAPagar = valorParcela + Number(totalJuros);
            novosJuros[parcela.id] = Number(totalJuros.toFixed(2)); // só os juros
            novosValoresPagamento[parcela.id] = Number(valorParaNovaParcela.toFixed(2)); // valor para nova parcela
            novosValoresPagos[parcela.id] = Number(totalJuros.toFixed(2)); // só os juros
            if (setValorJurosFixo) setValorJurosFixo(Number(totalJuros.toFixed(2))); // só os juros, igual ao campo de baixo
          } catch (err) {
            console.error('Erro ao buscar juros da parcela', parcela.id, err);
            novosJuros[parcela.id] = 0;
            novosValoresPagamento[parcela.id] = Number(parcela.valor_original || parcela.valor || 0);
            novosValoresPagos[parcela.id] = 0;
          }
        }
        setJurosFixosPorParcela(novosJuros);
        // Sempre atualiza valoresPagamento para mostrar o valor correto
        setValoresPagamento(prev => {
          const novosValores = { ...prev };
          Object.entries(novosValoresPagamento).forEach(([parcelaId, valor]) => {
            novosValores[parcelaId] = valor;
          });
          return novosValores;
        });
        setValoresPagos(novosValoresPagos); // Atualiza o novo estado de valoresPagamento
        setLoadingJurosRenovacao(false);
      }
    }
    buscarJurosRenovacao();
    if (!apenasJurosRenovarVencimento) {
      setJurosFixosPorParcela({});
    }
  }, [apenasJurosRenovarVencimento, selectedParcelas, setValorJurosFixo, selectedEmprestimo, valoresPagamento, setValoresPagamento, setValoresPagos]);

  // UseEffect para abrir o modal automaticamente e filtrar pelo id do empréstimo recebido via location.state
  useEffect(() => {
    if (location.state?.filtroEmprestimoId && location.state?.abrirModal) {
      setFiltrosAplicados(prev => ({
        ...prev,
        id_emprestimo: location.state.filtroEmprestimoId
      }));
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Novo useEffect para abrir o modal só quando o empréstimo filtrado estiver carregado:
  useEffect(() => {
    if (filtrosAplicados.id_emprestimo && emprestimos.length > 0) {
      const emp = emprestimos.find(e => String(e.id) === String(filtrosAplicados.id_emprestimo));
      if (emp) {
        setSelectedEmprestimoId(emp.id);
        setOpenDialog(true);
      }
    }
  }, [filtrosAplicados.id_emprestimo, emprestimos]);

  // Filtro de cliente em tempo real
  useEffect(() => {
    if (emprestimos.length > 0 && filtro) {
      const filtered = emprestimos.filter(emprestimo =>
        emprestimo.cliente?.nome?.toLowerCase().includes(filtro.toLowerCase())
      );
      setEmprestimosFiltrados(filtered);
    } else if (emprestimos.length > 0) {
      setEmprestimosFiltrados(emprestimos);
    }
  }, [filtro, emprestimos]);

  // Modificar o useEffect que monitora aplicarJurosAutomaticosCheckbox
  useEffect(() => {
    if (apenasJurosRenovarVencimento) {
      setTipoJuros('fixo');
      setValorJurosFixo(''); // Limpa ao marcar, será preenchido pelo cálculo dos juros
    } else {
      setValorJurosFixo('');
      setTipoJuros('percentual');
    }
  }, [apenasJurosRenovarVencimento]);

  // NOVO: useEffect para recalcular automaticamente quando valorJurosFixo mudar
  useEffect(() => {
    if (valorJurosFixo && valorJurosFixo > 0 && selectedParcelas.length > 0) {
      console.log('Pagamentos useEffect (valorJurosFixo): Recalculando valores automaticamente com juros:', valorJurosFixo);

      // Recalcular valores com os novos juros
      const timeoutId = setTimeout(() => {
        recalcularTodosValores({
          tipoJuros: 'fixo',
          jurosPercentual: 0,
          valorJurosFixo: valorJurosFixo,
          tipoDesconto: tipoDesconto,
          descontoPercentual: descontoPercentual,
          valorDescontoFixo: valorDescontoFixo,
          aplicarJurosAutomaticos: aplicarJurosAutomaticosCheckbox,
          forceRecalculate: true
        });
      }, 300); // Delay de 300ms para evitar recálculos excessivos

      return () => clearTimeout(timeoutId);
    }
  }, [valorJurosFixo, selectedParcelas, tipoDesconto, descontoPercentual, valorDescontoFixo, aplicarJurosAutomaticosCheckbox]);

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="contained" onClick={() => carregarDadosEmprestimos(true)}>
          Tentar Novamente
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{
      width: '100%',
      maxWidth: { xs: 400, sm: 600, md: 900 },
      mx: 'auto',
      bgcolor: '#fff',
      minHeight: '100vh',
      borderRadius: { xs: 0, sm: 2 },
      boxShadow: { xs: 0, sm: 2 },
      p: { xs: 1, md: 2 },
    }}>
      {/* Filtros */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, p: 1, '@media (max-width: 768px)': { flexDirection: 'column', alignItems: 'stretch' } }}>
        {/* Campo de busca por Cliente */}
        <TextField
          size="small"
          label="Cliente"
          variant="outlined"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ color: 'text.secondary', mr: 0.5 }} fontSize="small" />
            ),
          }}
          sx={{ width: { xs: '100%', sm: 180 }, flexShrink: 0, mb: { xs: 1, sm: 0 } }}
        />
        {/* Campo de Data Inicial (Vencimento) */}
        <TextField
          size="small"
          label="Vencimento De"
          type="date"
          value={dataInicial}
          onChange={(e) => setDataInicial(e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
          sx={{ width: { xs: '100%', sm: 150 }, flexShrink: 0, mb: { xs: 1, sm: 0 } }}
          variant="outlined"
        />
        {/* Campo de Data Final (Vencimento) */}
        <TextField
          size="small"
          label="Vencimento Até"
          type="date"
          value={dataFinal}
          onChange={(e) => setDataFinal(e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
          sx={{ width: { xs: '100%', sm: 150 }, flexShrink: 0, mb: { xs: 1, sm: 0 } }}
          variant="outlined"
        />
        {/* Botões Limpar e Aplicar */}
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="contained"
            color="warning"
            size="small"
            onClick={() => {
              setDataInicial('');
              setDataFinal('');
              setFiltro('');
              setFiltrosAplicados({
                dataInicial: '',
                dataFinal: '',
                filtroCliente: '',
                id_emprestimo: null
              });
              toast.info('Filtros limpos!');
            }}
            sx={{ width: { xs: '50%', sm: 90 }, flexShrink: 0 }}
            fullWidth={true}
          >
            LIMPAR
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              const newFiltros = {
                dataInicial,
                dataFinal,
                filtroCliente: filtro,
                id_emprestimo: filtrosAplicados.id_emprestimo
              };
              setFiltrosAplicados(newFiltros);
              toast.info('Aplicando filtros...');
              carregarDadosEmprestimos(true, newFiltros); // Passa os novos filtros diretamente
            }}
            sx={{ width: { xs: '50%', sm: 90 }, flexShrink: 0 }}
            fullWidth={true}
          >
            APLICAR
          </Button>

          {/* Botão Refresh */}
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={async () => {
              toast.info('Atualizando dados...');
              await carregarDadosEmprestimos(true);
              toast.success('Dados atualizados!');
            }}
            sx={{ width: { xs: '50%', sm: 90 }, flexShrink: 0 }}
            fullWidth={true}
            startIcon={<RefreshIcon />}
          >
            ATUALIZAR
          </Button>
        </Box>
      </Box>
      {/* Tabela de Empréstimos */}
      <TabelaEmprestimos
        emprestimosFiltrados={emprestimosFiltrados}
        handlePagamentoClick={handleOpenDialog}
        handleOpenHistorico={handleOpenHistorico}
      />

      {/* Modal de Pagamento */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="xl" fullWidth
        PaperProps={{
          sx: {
            '@media (max-width: 600px)': {
              minWidth: '100vw',
              maxWidth: '100vw',
              width: '100vw',
              borderRadius: 0,
              m: 0,
              p: 0,
            }
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem' }, p: { xs: 1, sm: 2 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem' }, color: '#1976d2' }}>
              Realizar Pagamento - {selectedEmprestimo?.cliente?.nome || selectedEmprestimo?.nome || selectedEmprestimo?.id || 'N/A'}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, alignItems: { xs: 'flex-start', sm: 'center' }, mt: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                Parcelas Pendentes
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={selecionarTodasParcelas}
                  fullWidth={true}
                  sx={{ minWidth: 0, fontSize: { xs: '0.9rem', sm: '1rem' }, py: 0.5 }}
                >
                  SELECIONAR TODAS
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={removerTodasSelecoes}
                  fullWidth={true}
                  sx={{ minWidth: 0, fontSize: { xs: '0.9rem', sm: '1rem' }, py: 0.5 }}
                >
                  REMOVER SELEÇÃO
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1, sm: 2 } }}>
          <Box>
            {/* 1. Tabela/lista de parcelas */}
            <TableContainer sx={{ width: '100%', overflowX: 'auto', mt: 1 }}>
              {/* MOBILE: Blocos verticais para cada parcela */}
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                {(selectedEmprestimo?.parcelas || []).sort((a, b) => new Date(a.data_vencimento) - new Date(b.data_vencimento)).map((parcela) => {
                  const valorAPagar = valoresPagamento[parcela.id] || valoresCalculados[parcela.id]?.valorFinal || parcela.valor_original || parcela.valor;
                  const valorPago = valoresPagos[parcela.id] || '';
                  let valorJurosAutomaticos = 0;
                  if (aplicarJurosAutomaticosCheckbox && parcela.status === 'atrasado') {
                    valorJurosAutomaticos = valoresCalculados[parcela.id]?.jurosAutomaticos || 0;
                  }
                  const isAtrasada = parcela.status === 'atrasado';
                  return (
                    <Box key={parcela.id} sx={{ mb: 2, p: 2, bgcolor: '#fff', borderRadius: 2, boxShadow: 1 }}>
                      <Box display="flex" alignItems="center" mb={1}>
                        <Checkbox
                          checked={selectedParcelas.some(p => p.id === parcela.id)}
                          onChange={() => toggleParcelaSelection(parcela)}
                          sx={{ mr: 1 }}
                        />
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{ color: isAtrasada ? 'error.main' : 'text.primary', mr: 1 }}
                        >
                          Parcela {parcela.numero_parcela}
                          {isAtrasada && (
                            <span style={{ fontWeight: 'normal', fontSize: '0.95em', marginLeft: 6 }}>
                              (ATRASADA)
                            </span>
                          )}
                        </Typography>
                        <Box flex={1} />
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(parcela.data_vencimento)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="body2">Valor: {formatCurrency(parcela.valor)}</Typography>
                        <Typography variant="body2">Juros Automáticos: {formatCurrency(valorJurosAutomaticos)}</Typography>
                        <Typography variant="body2">Valor a Pagar: {apenasJurosRenovarVencimento && selectedParcelas.some(p => p.id === parcela.id)
                          ? formatCurrency(parseFloat(parcela.valor || 0)) // Manter valor original da parcela
                          : formatCurrency(valorAPagar)}</Typography>
                        <TextField
                          label="Valor Pago"
                          type="number"
                          size="small"
                          value={valorPago}
                          onChange={(e) => {
                            if (!apenasJurosRenovarVencimento) handleValorPagoChange(parcela.id, e.target.value);
                          }}
                          inputProps={{ min: 0, step: 0.01 }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                            readOnly: apenasJurosRenovarVencimento || loadingJurosRenovacao
                          }}
                          sx={{ width: '100%', mb: 1, mt: 1 }}
                          disabled={!selectedParcelas.some(p => p.id === parcela.id)}
                        />
                        <TextField
                          label="Nova Data Vencimento"
                          type="date"
                          size="small"
                          value={novasDatasVencimento[parcela.id] || ''}
                          onChange={e => handleNovaDataVencimentoChange(parcela.id, e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          sx={{ width: '100%' }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
              {/* DESKTOP: Tabela tradicional */}
              <Table sx={{ display: { xs: 'none', md: 'table' }, minWidth: 400 }} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Selecionar</TableCell>
                    <TableCell>Parcela</TableCell>
                    <TableCell>Vencimento</TableCell>
                    <TableCell>Valor</TableCell>
                    <TableCell>Juros Automáticos</TableCell>
                    <TableCell>Valor a Pagar</TableCell>
                    <TableCell>Valor Pago</TableCell>
                    <TableCell>Nova Data Vencimento</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedEmprestimo?.parcelas?.sort((a, b) => new Date(a.data_vencimento) - new Date(b.data_vencimento)).map(parcela => {
                    const valorAPagar = valoresPagamento[parcela.id] || valoresCalculados[parcela.id]?.valorFinal || parcela.valor_original || parcela.valor;
                    const valorPago = valoresPagamento[parcela.id] || '';

                    // DEBUG: Log para verificar os valores
                    console.log(`DEBUG TABELA DESKTOP - Parcela ${parcela.id}:`, {
                      valoresPagamento: valoresPagamento[parcela.id],
                      valoresCalculados: valoresCalculados[parcela.id]?.valorFinal,
                      parcela_valor_original: parcela.valor_original,
                      parcela_valor: parcela.valor,
                      valorAPagar_calculado: valorAPagar
                    });

                    let valorJurosAutomaticos = 0;
                    if (aplicarJurosAutomaticosCheckbox && parcela.status === 'atrasado') {
                      valorJurosAutomaticos = valoresCalculados[parcela.id]?.jurosAutomaticos || 0;
                    }
                    const isAtrasada = parcela.status === 'atrasado';
                    return (
                      <TableRow key={parcela.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedParcelas.some(p => p.id === parcela.id)}
                            onChange={() => toggleParcelaSelection(parcela)}
                          />
                        </TableCell>
                        <TableCell>{parcela.numero_parcela}</TableCell>
                        <TableCell>{formatDate(parcela.data_vencimento)}</TableCell>
                        <TableCell>{formatCurrency(parcela.valor)}</TableCell>
                        <TableCell>
                          {aplicarJurosAutomaticosCheckbox && parcela.status === 'atrasado' ? (
                            <span style={{ color: '#1976d2', fontWeight: 'bold' }}>
                              {formatCurrency(valoresCalculados[parcela.id]?.jurosAutomaticos || 0)}
                            </span>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {apenasJurosRenovarVencimento && selectedParcelas.some(p => p.id === parcela.id)
                            ? formatCurrency(parseFloat(parcela.valor || 0)) // Manter valor original da parcela
                            : formatCurrency(valorAPagar)}
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={valoresPagos[parcela.id] || ''}
                            onChange={e => {
                              if (!apenasJurosRenovarVencimento) handleValorPagoChange(parcela.id, e.target.value);
                            }}
                            inputProps={{ min: 0, step: 0.01 }}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                              readOnly: loadingJurosRenovacao
                            }}
                            sx={{ width: { xs: 100, md: 160 } }}
                            disabled={!selectedParcelas.some(p => p.id === parcela.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            label="Nova Data Vencimento"
                            type="date"
                            size="small"
                            value={novasDatasVencimento[parcela.id] || ''}
                            onChange={e => handleNovaDataVencimentoChange(parcela.id, e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ ml: 1, minWidth: 120 }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {/* 2. Campos de Juros/Desconto/Aplicar/Checkbox */}
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: 2, md: 2 },
                alignItems: { xs: 'center', md: 'flex-end' },
                justifyContent: { xs: 'center', md: 'space-between' },
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1, alignItems: 'center', flex: 1 }}>
                <TextField
                  select
                  SelectProps={{ native: true }}
                  label="Tipo Juros"
                  value={tipoJuros}
                  onChange={e => setTipoJuros(e.target.value)}
                  size="small"
                  sx={{ width: { xs: '100%', md: 110 } }}
                >
                  <option value="percentual">%</option>
                  <option value="fixo">R$</option>
                </TextField>
                {tipoJuros === 'percentual' ? (
                  <TextField
                    label="Juros (%)"
                    type="number"
                    size="small"
                    value={jurosPercentual}
                    onChange={e => {
                      if (!apenasJurosRenovarVencimento) setJurosPercentual(e.target.value);
                    }}
                    sx={{ width: { xs: '100%', md: 110 } }}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  />
                ) : (
                  <TextField
                    label="Juros (R$)"
                    type="number"
                    size="small"
                    value={valorJurosFixo}
                    onChange={e => {
                      if (!apenasJurosRenovarVencimento) setValorJurosFixo(e.target.value);
                    }}
                    sx={{ width: { xs: '100%', md: 110 } }}
                    InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                  />
                )}
                <TextField
                  select
                  SelectProps={{ native: true }}
                  label="Tipo Desconto"
                  value={tipoDesconto}
                  onChange={e => setTipoDesconto(e.target.value)}
                  size="small"
                  sx={{ width: { xs: '100%', md: 110 } }}
                >
                  <option value="percentual">%</option>
                  <option value="fixo">R$</option>
                </TextField>
                <TextField
                  label={tipoDesconto === 'percentual' ? 'Desconto (%)' : 'Desconto (R$)'}
                  type="number"
                  size="small"
                  value={tipoDesconto === 'percentual' ? descontoPercentual : valorDescontoFixo}
                  onChange={(e) => {
                    if (tipoDesconto === 'percentual') {
                      setDescontoPercentual(e.target.value);
                    } else {
                      setValorDescontoFixo(e.target.value);
                    }
                  }}
                  sx={{ width: { xs: '100%', md: 110 } }}
                />
                <Button
                  variant="outlined"
                  onClick={handleAplicarTaxas}
                  sx={{ width: { xs: '100%', md: 100 }, minWidth: 80 }}
                  disabled={apenasJurosRenovarVencimento}
                >
                  Aplicar
                </Button>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={aplicarJurosAutomaticosCheckbox}
                      onChange={(event) => setAplicarJurosAutomaticosCheckbox(event.target.checked)}
                    />
                  }
                  label="Aplicar Juros Automáticos"
                  sx={{ ml: { xs: 0, md: 2 } }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={apenasJurosRenovarVencimento}
                      onChange={e => setApenasJurosRenovarVencimento(e.target.checked)}
                    />
                  }
                  label="Apenas juros e renovar vencimento"
                  sx={{ ml: { xs: 0, md: 2 } }}
                />
              </Box>
              {/* 3. Resumo detalhado das parcelas selecionadas */}
              {selectedParcelas.length > 0 && (
                <Box sx={{ mt: 2, mb: 2, width: '100%' }}>
                  {/* DESKTOP: Tabela detalhada */}
                  <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Parcela</TableCell>
                          <TableCell>Dias em Atraso</TableCell>
                          <TableCell>Juros (%)</TableCell>
                          <TableCell>Periodicidade</TableCell>
                          <TableCell>Juros Automáticos</TableCell>
                          <TableCell>Valor Juros</TableCell>
                          <TableCell>Desconto</TableCell>
                          <TableCell>Valor Final</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedParcelas.map((parcela) => {
                          const detalhes = (valoresCalculados[parcela.id]?.detalhes || valoresCalculados[parcela.id] || {});
                          let periodicidade = '-';
                          if (detalhes.periodicidade) {
                            if (detalhes.periodicidade === 'mensal') periodicidade = 'ao mês';
                            else if (detalhes.periodicidade === 'diario') periodicidade = 'ao dia';
                            else if (detalhes.periodicidade === 'semanal') periodicidade = 'à semana';
                            else periodicidade = `ao ${detalhes.periodicidade}`;
                          }
                          return (
                            <TableRow key={parcela.id}>
                              <TableCell>{parcela.numero_parcela}</TableCell>
                              <TableCell>{calcularDiasAtraso(parcela)}</TableCell>
                              <TableCell>
                                {(() => {
                                  if (!detalhes || !detalhes.regra) return '-';
                                  if (detalhes.regra.startsWith('R$')) {
                                    return detalhes.regra.split(' ')[1]?.replace(',', '.') || '';
                                  }
                                  if (detalhes.regra.endsWith('%')) {
                                    return detalhes.regra.split(' ')[0].replace('%', '');
                                  }
                                  return '-';
                                })()}
                              </TableCell>
                              <TableCell>{periodicidade}</TableCell>
                              <TableCell>{detalhes.jurosAutomaticos ? `${formatCurrency(detalhes.jurosAutomaticos)}${detalhes.taxa ? ` (${detalhes.taxa}${detalhes.periodicidade ? ` ${periodicidade}` : ''})` : ''}${detalhes.descricaoRegra ? ` - ${detalhes.descricaoRegra}` : ''}` : '-'}</TableCell>
                              <TableCell>
                                {apenasJurosRenovarVencimento && selectedParcelas.some(p => p.id === parcela.id)
                                  ? formatCurrency(jurosFixosPorParcela[parcela.id] || 0)
                                  : formatCurrency(detalhes.jurosManuais || 0)}
                              </TableCell>
                              <TableCell>{formatCurrency(detalhes.desconto || 0)}</TableCell>
                              <TableCell>
                                {apenasJurosRenovarVencimento && selectedParcelas.some(p => p.id === parcela.id)
                                  ? formatCurrency(parseFloat(parcela.valor || 0)) // Manter valor original da parcela
                                  : formatCurrency(detalhes.valorFinal || parcela.valor)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {/* MOBILE: Cards detalhados */}
                  <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                    {selectedParcelas.map((parcela) => {
                      const detalhes = (valoresCalculados[parcela.id]?.detalhes || valoresCalculados[parcela.id] || {});
                      let periodicidade = '-';
                      if (detalhes.periodicidade) {
                        if (detalhes.periodicidade === 'mensal') periodicidade = 'ao mês';
                        else if (detalhes.periodicidade === 'diario') periodicidade = 'ao dia';
                        else if (detalhes.periodicidade === 'semanal') periodicidade = 'à semana';
                        else periodicidade = `ao ${detalhes.periodicidade}`;
                      }
                      return (
                        <Box key={parcela.id} sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                          <Typography variant="body2"><b>Parcela:</b> {parcela.numero_parcela}</Typography>
                          <Typography variant="body2"><b>Dias em Atraso:</b> {calcularDiasAtraso(parcela)}</Typography>
                          <Typography variant="body2"><b>Juros (%):</b> {(() => {
                            if (!detalhes || !detalhes.regra) return '-';
                            if (detalhes.regra.startsWith('R$')) {
                              return detalhes.regra.split(' ')[1]?.replace(',', '.') || '';
                            }
                            if (detalhes.regra.endsWith('%')) {
                              return detalhes.regra.split(' ')[0].replace('%', '');
                            }
                            return '-';
                          })()}</Typography>
                          <Typography variant="body2"><b>Periodicidade:</b> {periodicidade}</Typography>
                          <Typography variant="body2"><b>Juros Automáticos:</b> {detalhes.jurosAutomaticos ? `${formatCurrency(detalhes.jurosAutomaticos)}${detalhes.taxa ? ` (${detalhes.taxa}${detalhes.periodicidade ? ` ${periodicidade}` : ''})` : ''}${detalhes.descricaoRegra ? ` - ${detalhes.descricaoRegra}` : ''}` : '-'}</Typography>
                          <Typography variant="body2"><b>Valor Juros:</b> {apenasJurosRenovarVencimento && selectedParcelas.some(p => p.id === parcela.id)
                            ? formatCurrency(jurosFixosPorParcela[parcela.id] || 0)
                            : formatCurrency(detalhes.jurosManuais || 0)}</Typography>
                          <Typography variant="body2"><b>Desconto:</b> {formatCurrency(detalhes.desconto || 0)}</Typography>
                          <Typography variant="body2"><b>Valor Final:</b> {apenasJurosRenovarVencimento && selectedParcelas.some(p => p.id === parcela.id)
                            ? formatCurrency(parseFloat(parcela.valor || 0)) // Manter valor original da parcela
                            : formatCurrency(detalhes.valorFinal || parcela.valor)}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
              {/* 4. Totais e botões */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: 'center',
                  justifyContent: { xs: 'center', md: 'space-between' },
                  gap: { xs: 1.5, md: 2 },
                  mt: 2,
                  width: '100%',
                }}
              >
                <Typography variant="h6" align="center" sx={{ minWidth: 180 }}>
                  Total Pago: {formatCurrency(
                    apenasJurosRenovarVencimento
                      ? selectedParcelas.reduce((acc, parcela) => acc + (parseFloat(valoresPagos[parcela.id]) || 0), 0)
                      : selectedParcelas.reduce((acc, parcela) => acc + (parseFloat(valoresPagamento[parcela.id]) || 0), 0)
                  )}
                </Typography>
                <Typography variant="h6" align="center" sx={{ minWidth: 180 }}>
                  Valor Total: {formatCurrency(calcularValorTotal())}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handlePagamento}
                  sx={{ fontSize: '1.1rem', py: 1, minWidth: 180 }}
                >
                  Confirmar Pagamento
                </Button>
                <Button
                  onClick={handleCloseDialog}
                  color="secondary"
                  sx={{ fontSize: '1rem', py: 1, minWidth: 140 }}
                >
                  Cancelar
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Modal de Histórico de Pagamentos */}
      <HistoricoPagamento
        open={openHistoricoDialog}
        onClose={handleCloseHistoricoDialog}
        historicoPagamento={historicoPagamento}
      />
    </Box>
  );
}